import {type RunsDataQueryConfig} from '../../../../containers/RunsDataLoader';
import * as Filter from '../../../../util/filters';
import {isSystemMetric} from '../../../../util/metrics';
import {type DataDogPanelType} from '../../../../util/panelTypes';
import {sortToOrderString} from '../../../../util/queryts';
import {type SingleQuery} from '../../../../util/queryTypes';
import {GROUP_BY_ALL_KEY} from '../../../../util/runTypes';
import {
  applyRunExclusions,
  calculateRunsLimit,
  getBaselineRunQueryParams,
  getPinnedRunsQueryParams,
} from './filterHelpers';

export type BucketedQueryType =
  | 'BucketedQuery'
  | 'BucketedQueryId'
  | 'BucketedBackendLinesQuery'
  | 'BucketedBackendLinesQueryId'
  | 'GroupedBucketedQuery'
  | 'GroupedBucketedQueryId'
  | 'GroupedBucketedBackendLinesQuery';

export type QueryVars = {
  baselineRunFilters: string | undefined;
  bins?: number; // not used by the backend, included for datadog segmentation
  bucketedHistorySpecs: string[];
  configKeys: string[] | undefined;
  enableBasic: boolean;
  enableConfig: boolean;
  enableSummary: boolean;
  entityName: string;
  expressions: string[] | undefined;
  filters: string;
  groupKeys: string[];
  groupLevel: number;
  includeBaselineRun: boolean;
  includePinnedRuns: boolean;
  internalId: string;
  isSystemMetrics: boolean | null; // not used, included for datadog segmentation
  limit: number;
  maxGroups: number | undefined;
  order: string;
  panelType: DataDogPanelType; // client-only, like bins
  pinnedRunFilters: string | undefined;
  pinnedRunsLimit: number;
  projectName: string;
  summaryKeys: string[] | undefined;
  timeout?: number; // Frontend-specified timeout in seconds for progressive loading
};

export function queryVars(
  runsDataQuery: Exclude<RunsDataQueryConfig, 'runsetQueries'>,
  query: SingleQuery,
  nBuckets: number,
  queryType: BucketedQueryType,
  expressions: string[] | undefined,
  panelType: DataDogPanelType,
  timeout?: number
): QueryVars {
  const order = sortToOrderString(query.sort);

  let limit = runsDataQuery.page?.size || 10;
  if (Array.isArray(limit)) {
    console.error('Unexpected query limit of type array', {
      entityName: runsDataQuery.entityName,
      projectName: runsDataQuery.projectName,
    });
    limit = limit.flat(Infinity)[0] ?? 10;
  }

  // Replace config.None with GROUP_BY_ALL_KEY because config.None is not a valid group key when grouping in the backend
  // This happens when selecting "None" in the group by dropdown
  // TODO: Move this replacement upstream to where groupKeys are initially populated, once we have sufficient test
  // coverage to safely make that change.
  const groupKeys = [
    'GroupedBucketedQuery',
    'GroupedBucketedQueryWithInternalId',
    'GroupedBucketedBackendLinesQuery',
  ].includes(queryType)
    ? (runsDataQuery.groupKeys?.map(k =>
        k === 'config.None' ? 'config.' + GROUP_BY_ALL_KEY.name : k
      ) ?? [])
    : [];

  // In single-run mode: fetch specified run only (no baseline or pinned)
  // In multi-run mode: fetch filtered runs + baseline + pinned (if not grouped)
  const isSingleRunMode = !!runsDataQuery.runName;
  const isGrouped = groupKeys.length > 0;

  // Get baseline and pinned run query parameters
  const baseline = getBaselineRunQueryParams({
    baselineRunId: query.baselineRunId,
    isSingleRunMode,
    isGrouped,
    entityName: query.entityName,
    projectName: query.projectName,
  });
  const pinned = getPinnedRunsQueryParams({
    pinnedRunIds: query.pinnedRunIds,
    isSingleRunMode,
    pageSize: limit,
    includeBaseline: baseline.include,
    isGrouped,
    baselineRunName: baseline.runName ?? undefined,
    entityName: query.entityName,
    projectName: query.projectName,
  });

  // Apply exclusions to prevent overlap in main query
  const filtersWithExclusions = applyRunExclusions(query.filters, {
    isSingleRunMode,
    baselineRunName: baseline.runName,
    pinnedRunNames: pinned.runNames,
  });

  const filters = JSON.stringify(Filter.toMongo(filtersWithExclusions));

  return {
    bins: nBuckets,
    bucketedHistorySpecs:
      runsDataQuery.bucketedHistorySpecs?.map(bhs =>
        JSON.stringify({...bhs, bins: nBuckets, xAxis: bhs.xAxis ?? '_step'})
      ) ?? [],
    configKeys: runsDataQuery.configKeys,
    enableBasic: true,
    enableConfig: runsDataQuery.configKeys != null,
    enableSummary: runsDataQuery.summaryKeys != null,
    entityName: query.entityName,
    expressions,
    filters,
    groupKeys,
    groupLevel: 0,
    internalId: query.internalProjectId ?? '',
    isSystemMetrics:
      runsDataQuery.bucketedHistorySpecs?.every(bhs =>
        bhs.keys.some(isSystemMetric)
      ) ?? null,
    limit: calculateRunsLimit(
      isSingleRunMode,
      limit,
      baseline.include,
      pinned.limitedCount,
      query.entityName,
      query.projectName
    ),
    maxGroups: query.maxGroups,
    order,
    panelType,
    projectName: query.projectName,
    summaryKeys: runsDataQuery.summaryKeys,
    baselineRunFilters: baseline.filters,
    pinnedRunFilters: pinned.filters,
    // Maximum number of pinned runs to fetch.
    // Set to limitedCount (the number of run names after slicing to fit within pageSize,
    // accounting for baseline run if included) to ensure we never fetch more pinned runs
    // than we've allocated space for.
    pinnedRunsLimit: pinned.include ? pinned.limitedCount : 0,
    includeBaselineRun: baseline.include,
    includePinnedRuns: pinned.include,
    timeout,
  };
}
